import React, { useRef } from "react";
import { Trophy } from "lucide-react";
import CopyImageChip from "./CopyImageChip";
import { buildChampionPath, formatWeekRange } from "./cupStats";

const STATUS_COPY = {
    draft: (cup) => ({
        headline: "The Cup awaits",
        sub: `Group Stage kicks off in week ${cup.config?.start_week}`,
    }),
    group_stage: (cup) => ({
        headline: "Group Stage underway",
        sub: `Knockouts begin week ${cup.schedule?.knockout_rounds?.[0]?.start_week}`,
    }),
    knockout: (cup) => {
        const final = cup.schedule?.knockout_rounds?.slice(-1)[0];
        return {
            headline: "Knockouts underway",
            sub: final
                ? `Final: ${formatWeekRange(final.start_week, final.end_week)}`
                : "",
        };
    },
};

/**
 * Dark spotlight tile. Shows the champion once crowned; before that, the
 * current phase of the Cup.
 */
const ChampionTile = ({ cup }) => {
    const ref = useRef(null);
    const champion = cup?.champion;
    const path = buildChampionPath(cup);

    return (
        <div
            ref={ref}
            className="relative flex flex-col justify-center overflow-hidden rounded-2xl border border-mustard/50 bg-gradient-to-br from-[#16291e] via-forest to-[#2a4a36] px-6 py-7 text-center text-white shadow-card"
        >
            <div className="absolute right-2.5 top-2.5">
                <CopyImageChip
                    targetRef={ref}
                    fileName="league-cup-champion"
                    label=""
                    variant="dark"
                />
            </div>

            {champion ? (
                <div>
                    <div className="text-5xl drop-shadow-[0_6px_16px_rgba(212,165,32,0.45)]">
                        🏆
                    </div>
                    <p className="mt-2.5 text-[10px] font-sans font-extrabold uppercase tracking-[0.3em] text-mustard">
                        Cup Champion
                    </p>
                    <h2 className="mt-1.5 font-display text-[27px] font-black leading-tight">
                        {champion.owner_name || champion.team_name}
                    </h2>
                    {champion.owner_name &&
                        champion.team_name && (
                            <p className="text-[12.5px] font-sans font-bold text-white/65">
                                {champion.team_name}
                            </p>
                        )}
                    {path && (
                        <p className="mt-3.5 text-[11px] font-sans font-bold text-white/50">
                            {path.origin}
                            {path.legs.map((leg) => (
                                <span key={leg.label}>
                                    {" → "}
                                    {leg.label}{" "}
                                    <b className="text-mustard">+{leg.margin}</b>
                                </span>
                            ))}
                        </p>
                    )}
                </div>
            ) : (
                <div>
                    <Trophy className="mx-auto h-11 w-11 text-mustard/70" />
                    <p className="mt-3 text-[10px] font-sans font-extrabold uppercase tracking-[0.3em] text-mustard">
                        Cup Champion · TBD
                    </p>
                    <h2 className="mt-1.5 font-display text-2xl font-black leading-tight">
                        {(STATUS_COPY[cup?.status] || STATUS_COPY.draft)(cup || {})
                            .headline}
                    </h2>
                    <p className="mt-1 text-[12.5px] font-sans font-bold text-white/60">
                        {(STATUS_COPY[cup?.status] || STATUS_COPY.draft)(cup || {}).sub}
                    </p>
                </div>
            )}
        </div>
    );
};

export default ChampionTile;
